import { ApiBaseService } from './ApiBaseService';

export interface SystemAiModelResponse {
  id: string;
  aiCompany: string;
  modelName: string;
  modelType: string;
  maxInputToken: number | null;
  maxOutputToken: number | null;
  contextWindow: number | null;
  isVisionModel: boolean;
  isReasoning: boolean;
  supportsStreaming: boolean;
  isActive: boolean;
  isDeprecated: boolean;
  description: string | null;
}

export interface ModelPriceResponse {
  id: string;
  model: string;
  fluxConversion: number;
  systemAiModelId: string | null;
  systemAiModel: SystemAiModelResponse | null;
}

export interface ModelPriceListResponse {
  status: string;
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  data: ModelPriceResponse[];
}

export interface ModelPriceSingleResponse {
  status: string;
  data: ModelPriceResponse;
}

export interface UpdateModelPricePayload {
  fluxConversion?: number;
  systemAiModelId?: string;
  createSystemAiModel?: boolean;
  aiCompany?: string;
  modelName?: string;
  modelType?: string;
  maxInputToken?: number | null;
  maxOutputToken?: number | null;
  contextWindow?: number | null;
  isVisionModel?: boolean;
  isReasoning?: boolean;
  supportsStreaming?: boolean;
  isActive?: boolean;
  isDeprecated?: boolean;
  description?: string;
}

export interface UpdateSystemAiModelPayload {
  maxOutputToken?: number;
  supportsStreaming?: boolean;
  isDeprecated?: boolean;
  isVisionModel?: boolean;
  isActive?: boolean;
}

export interface CreateModelPricePayload {
  model: string;
  fluxConversion: number;
  createSystemAiModel?: boolean;
  systemAiModelId?: string;
  aiCompany?: string;
  modelName?: string;
  modelType?: string;
  maxInputToken?: number | null;
  maxOutputToken?: number | null;
  contextWindow?: number | null;
  isVisionModel?: boolean;
  isReasoning?: boolean;
  supportsStreaming?: boolean;
  isActive?: boolean;
  isDeprecated?: boolean;
  description?: string;
}

export interface SystemAiModelSearchResult {
  status: string;
  data: SystemAiModelResponse[];
}

export interface ModelPriceFilters {
  search?: string;
  isActive?: boolean | null;
  isDeprecated?: boolean | null;
  isVisionModel?: boolean | null;
  supportsStreaming?: boolean | null;
  modelType?: string;
}

class AdminAiModelsService {
  static async getAll(
    page: number = 1,
    pageSize: number = 25,
    filters: ModelPriceFilters = {},
  ): Promise<ModelPriceListResponse> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (filters.search?.trim()) {
      params.set('search', filters.search.trim());
    }
    if (filters.isActive !== undefined && filters.isActive !== null) {
      params.set('isActive', String(filters.isActive));
    }
    if (filters.isDeprecated !== undefined && filters.isDeprecated !== null) {
      params.set('isDeprecated', String(filters.isDeprecated));
    }
    if (filters.isVisionModel !== undefined && filters.isVisionModel !== null) {
      params.set('isVisionModel', String(filters.isVisionModel));
    }
    if (filters.supportsStreaming !== undefined && filters.supportsStreaming !== null) {
      params.set('supportsStreaming', String(filters.supportsStreaming));
    }
    if (filters.modelType?.trim()) {
      params.set('modelType', filters.modelType.trim());
    }
    return ApiBaseService.get<ModelPriceListResponse>(
      `/model_price?${params.toString()}`,
    );
  }

  static async getOne(id: string): Promise<ModelPriceSingleResponse> {
    return ApiBaseService.get<ModelPriceSingleResponse>(`/model_price/${id}`);
  }

  static async updateModelPrice(
    id: string,
    data: UpdateModelPricePayload,
  ): Promise<any> {
    return ApiBaseService.patch(`/model_price/${id}`, data);
  }

  static async updateSystemAiModel(
    modelPriceId: string,
    data: UpdateSystemAiModelPayload,
  ): Promise<any> {
    return ApiBaseService.patch(
      `/model_price/${modelPriceId}/system-ai-model`,
      data,
    );
  }

  static async create(
    data: CreateModelPricePayload,
  ): Promise<ModelPriceSingleResponse> {
    return ApiBaseService.post<ModelPriceSingleResponse>(
      '/model_price',
      data,
      {},
    );
  }

  static async searchSystemAiModels(
    search: string = '',
  ): Promise<SystemAiModelResponse[]> {
    const params = new URLSearchParams();
    if (search.trim()) {
      params.set('search', search.trim());
    }
    const response = await ApiBaseService.get<SystemAiModelSearchResult>(
      `/model_price/system-ai-models/search?${params.toString()}`,
    );
    return Array.isArray(response.data) ? response.data : [];
  }
}

export default AdminAiModelsService;
